/**
 * The calm header of a screen outside the shell (TMMAPP-0025, restyled TMMAPP-0036; kept from the
 * retired wizard frame by TMMAPP-0143): the brand mark and product name, language and theme as icon
 * menus, and — with a session — the avatar opening the shell's `AccountMenu`.
 */
import { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BrandLogo, BrandMark } from '@/components/BrandLogo';
import { LanguageMenuButton } from '@/components/LanguageMenuButton';
import { ThemeModeMenuButton } from '@/components/ThemeModeMenuButton';
import { useOptionalMe } from '@/session/meHooks';
import { AccountMenu } from '@/shell/AccountMenu';
import { useBarMinHeight } from '@/shell/barHeight';
import { initialsOf } from '@/shell/initials';
import { resolveTokens } from '@/theme/theme';
import { useBrand } from '@/theme/useBrand';

export function WizardHeader() {
  const { t } = useTranslation();
  const me = useOptionalMe();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const barMinHeight = useBarMinHeight();
  const { assets, productName } = useBrand();
  const dark = theme.palette.mode === 'dark';
  const hasLogo = (dark ? (assets.logoReversed ?? assets.logo) : assets.logo) !== null;

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        bgcolor: tokens.surface,
        borderBottom: `1px solid ${tokens.lineStrong}`,
        color: 'text.primary',
      }}
    >
      <Toolbar sx={{ gap: 1, minHeight: barMinHeight }}>
        {hasLogo ? (
          <BrandLogo reversed={dark} fallback="mark" height={32} />
        ) : (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
            <BrandMark size={28} />
            <Typography component="span" variant="subtitle1" noWrap>
              {productName}
            </Typography>
          </Stack>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <LanguageMenuButton />
        <ThemeModeMenuButton />
        {me !== null && (
          <IconButton
            aria-label={t('shell.accountMenu')}
            aria-haspopup="menu"
            aria-expanded={menuAnchor !== null}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ p: 0.5, marginInlineStart: 0.5 }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                typography: 'caption',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              {initialsOf(me.user.displayName)}
            </Avatar>
          </IconButton>
        )}
      </Toolbar>
      {me !== null && <AccountMenu anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)} />}
    </AppBar>
  );
}
